import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@adco-rooms/shared';
import { env } from '@/lib/env';

/** Fully typed socket instance shared by the realtime provider and hooks. */
export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface CreateSocketOptions {
  /** Resolves the current access token; called again on every reconnect. */
  getToken: () => Promise<string | null>;
}

/**
 * Creates a socket.io client with exponential backoff.
 *
 * The connection is authenticated per attempt through the `auth` callback so a
 * refreshed token is picked up automatically after a long disconnection.
 * Realtime is a *hint*: consumers always re-validate against the REST API.
 */
export function createSocket({ getToken }: CreateSocketOptions): AppSocket {
  const socket: AppSocket = io(env.socketUrl || undefined, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Number.POSITIVE_INFINITY,
    reconnectionDelay: 800,
    reconnectionDelayMax: 15_000,
    randomizationFactor: 0.5,
    timeout: 10_000,
    withCredentials: false,
    auth: (callback: (payload: Record<string, unknown>) => void) => {
      void getToken()
        .then((token) => callback({ token: token ?? null }))
        .catch(() => callback({ token: null }));
    },
  });

  return socket;
}
